/**
 * analytics.ts — Firebase Analytics integration (native + web).
 *
 * Ported from v1's `lib/firebase/analytics.ts`.
 *
 * Platform routing:
 *   - Native (iOS/Android): @capacitor-firebase/analytics plugin (no cookies)
 *   - Web: Firebase Web SDK with GA4 Consent Mode
 *
 * Consent strategy:
 *   - Native: No consent needed — iOS ATT handles device-level tracking.
 *   - Web pre-login: analytics_storage='denied' (cookieless, aggregated data).
 *   - Web post-login: analytics_storage='granted' (user accepted ToS).
 *   This matches how Vercel, Linear, etc. handle it.
 *
 * S14: Firebase SDK is dynamically imported to avoid ~80-120 KB in main bundle.
 */

import { Capacitor } from "@capacitor/core"
import type { FirebaseApp } from "firebase/app"
import type { Analytics } from "firebase/analytics"

// ---------------------------------------------------------------------------
// Firebase config — same project as v1
// ---------------------------------------------------------------------------

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDZ51lAtzKfjYeOu2yZTeGn0hHhMN_5OZg",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "to-to-do-626af.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "to-to-do-626af",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "to-to-do-626af.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "957526079869",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:957526079869:web:6013eb55d4a502b1dcb414",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-VETDLVGK62",
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let webApp: FirebaseApp | undefined
let webAnalytics: Analytics | undefined
let initialized = false

/** Pre-init event buffer — flushed once initializeAnalytics() succeeds */
const preInitBuffer: Array<{ eventName: string; params?: Record<string, string | number | boolean> }> = []
const MAX_BUFFER = 50

// ---------------------------------------------------------------------------
// Core API
// ---------------------------------------------------------------------------

/**
 * Initialize Firebase Analytics. Call once at app startup.
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * - Native: Full analytics, no consent needed (iOS ATT handles it).
 * - Web: Starts in cookieless mode (analytics_storage: 'denied').
 *   Call `grantAnalyticsConsent()` after user accepts ToS (login).
 */
export async function initializeAnalytics(): Promise<void> {
  if (initialized) return

  try {
    if (Capacitor.isNativePlatform()) {
      // Native: enable the Capacitor Firebase Analytics plugin
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      )
      await FirebaseAnalytics.setEnabled({ enabled: true })
    } else {
      // S14: Dynamic import — Firebase SDK loaded on-demand, not at startup
      const [{ initializeApp }, { getAnalytics, setConsent: setConsentFn }] =
        await Promise.all([
          import("firebase/app"),
          import("firebase/analytics"),
        ])

      // Web: start with analytics_storage denied (cookieless / aggregated)
      // This allows page_view and events to be collected without setting
      // cookies — GA4 models the data server-side. No cookie banner needed.
      setConsentFn({
        analytics_storage: "denied",
        ad_storage: "denied",
      })

      webApp = initializeApp(firebaseConfig)
      webAnalytics = getAnalytics(webApp)
    }

    initialized = true
    console.log("[Analytics] Initialized", Capacitor.isNativePlatform() ? "(native)" : "(web)")

    // Flush buffered pre-init events
    for (const buffered of preInitBuffer.splice(0)) {
      void logAnalyticsEvent(buffered.eventName, buffered.params)
    }
  } catch (error) {
    console.warn("[Analytics] Initialization failed:", error)
  }
}

/**
 * Upgrade web analytics consent to 'granted'.
 * Call after the user logs in (they accepted ToS at sign-up).
 * No-op on native (consent is device-level via iOS ATT).
 */
export async function grantAnalyticsConsent(): Promise<void> {
  if (Capacitor.isNativePlatform()) return
  const { setConsent } = await import("firebase/analytics")
  setConsent({
    analytics_storage: "granted",
  })
}

/**
 * Log a custom analytics event.
 *
 * @param eventName — Event name (max 40 chars, alphanumeric + underscore)
 * @param params — Key-value pairs (max 25 per event)
 */
export async function logAnalyticsEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): Promise<void> {
  if (!initialized) {
    if (preInitBuffer.length < MAX_BUFFER) {
      preInitBuffer.push({ eventName, params })
    }
    return
  }

  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      )
      await FirebaseAnalytics.logEvent({ name: eventName, params: params ?? {} })
    } else if (webAnalytics) {
      const { logEvent } = await import("firebase/analytics")
      logEvent(webAnalytics, eventName, params ?? {})
    }
  } catch {
    // Non-critical — swallow analytics errors
  }
}

/**
 * Set user ID for user-scoped analytics.
 */
export async function setAnalyticsUserId(userId: string): Promise<void> {
  if (!initialized) return

  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      )
      await FirebaseAnalytics.setUserId({ userId })
    } else if (webAnalytics) {
      const { setUserId } = await import("firebase/analytics")
      setUserId(webAnalytics, userId)
    }
  } catch {
    // Non-critical
  }
}

/**
 * Set user properties for audience segmentation.
 */
export async function setAnalyticsUserProperties(
  properties: Record<string, string>,
): Promise<void> {
  if (!initialized) return

  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      )
      for (const [key, value] of Object.entries(properties)) {
        await FirebaseAnalytics.setUserProperty({ key, value })
      }
    } else if (webAnalytics) {
      const { setUserProperties } = await import("firebase/analytics")
      setUserProperties(webAnalytics, properties)
    }
  } catch {
    // Non-critical
  }
}

/**
 * Log a screen view event. On native, automatic screen tracking
 * may also fire — this provides explicit control.
 */
export async function logScreenView(screenName: string): Promise<void> {
  if (!initialized) return

  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      )
      await FirebaseAnalytics.logEvent({
        name: "screen_view",
        params: { screen_name: screenName },
      })
    } else if (webAnalytics) {
      const { logEvent } = await import("firebase/analytics")
      logEvent(webAnalytics, "screen_view", {
        firebase_screen: screenName,
        firebase_screen_class: screenName,
      })
    }
  } catch {
    // Non-critical
  }
}

/**
 * Log a purchase event (predefined Firebase event).
 */
export async function logPurchaseEvent(
  value: number,
  currency: string = "SEK",
  items?: Array<{ name: string; quantity: number }>,
): Promise<void> {
  await logAnalyticsEvent("purchase", {
    value,
    currency,
    ...(items ? { items: JSON.stringify(items) } : {}),
  })
}

/**
 * Enable or disable analytics collection (GDPR consent).
 *
 * Can be called before or after initialization.
 */
export async function setAnalyticsCollectionEnabled(
  enabled: boolean,
): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { FirebaseAnalytics } = await import(
        "@capacitor-firebase/analytics"
      )
      await FirebaseAnalytics.setEnabled({ enabled })
    } else if (webAnalytics) {
      const { setAnalyticsCollectionEnabled: setWebEnabled } = await import(
        "firebase/analytics"
      )
      setWebEnabled(webAnalytics, enabled)
    }
  } catch {
    // Non-critical
  }
}
